import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { FetchWithAuth, BodyWeightLog } from '../../types';
import { API_URL } from '../../utils';
import { useLanguage } from '../../i18n/LanguageContext';

interface BodyWeightProgressionViewProps {
  fetchWithAuth: FetchWithAuth;
  onServiceUnavailable?: () => void;
}

// Extended interface for chart data with calculated fat mass
interface ChartDataPoint extends BodyWeightLog {
  fat_mass?: number;
}

export const BodyWeightProgressionView: React.FC<BodyWeightProgressionViewProps> = ({ fetchWithAuth, onServiceUnavailable }) => {
  const [logs, setLogs] = useState<BodyWeightLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useLanguage();

  useEffect(() => {
    setLoading(true);
    fetchWithAuth(`${API_URL}/bodyweight/all`)
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setLogs(data.sort((a, b) => a.date.localeCompare(b.date)));
        } else {
          setLogs([]);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching body weight logs:', err);
        if (err.isServiceUnavailable && onServiceUnavailable) {
          onServiceUnavailable();
        } else {
          setError(t('bodyweight.error'));
        }
        setLoading(false);
      });
  }, [fetchWithAuth, t, onServiceUnavailable]);

  if (loading) return <Typography>{t('bodyweight.loading')}</Typography>;
  if (error) return <Typography color="error">{error}</Typography>;
  if (!logs.length) return <Typography>{t('bodyweight.noData')}</Typography>;

  // Calculate fat mass for logs that have fat percentage data
  const fatMassLogs: ChartDataPoint[] = logs
    .filter(log => log.fat_percentage !== null && log.fat_percentage !== undefined)
    .map(log => ({
      ...log,
      fat_mass: log.weight * (log.fat_percentage! / 100)
    }));

  const bmiLogs = logs.filter(log => log.bmi !== null && log.bmi !== undefined);

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h5" gutterBottom>{t('bodyweight.progression')}</Typography>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={logs} margin={{ top: 16, right: 16, left: 0, bottom: 16 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" tickFormatter={(d) => new Date(d).toLocaleDateString()} />
          <YAxis domain={['auto', 'auto']} />
          <Tooltip labelFormatter={(d) => new Date(d).toLocaleDateString()} />
          <Line type="monotone" dataKey="weight" stroke="#1976d2" dot />
        </LineChart>
      </ResponsiveContainer>

      {fatMassLogs.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom>{t('bodyweight.fatMassProgression')}</Typography>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={fatMassLogs} margin={{ top: 16, right: 16, left: 0, bottom: 16 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tickFormatter={(d) => new Date(d).toLocaleDateString()} />
              <YAxis domain={['auto', 'auto']} />
              <Tooltip 
                labelFormatter={(d) => new Date(d).toLocaleDateString()}
                formatter={(value, name) => {
                  if (name === 'fat_mass' && typeof value === 'number') {
                    return [`${value.toFixed(1)} kg`, t('bodyweight.fatMass')];
                  }
                  return [value, name];
                }}
              />
              <Line type="monotone" dataKey="fat_mass" stroke="#f44336" dot />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}

      {bmiLogs.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom>{t('bodyweight.bmiProgression')}</Typography>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={bmiLogs} margin={{ top: 16, right: 16, left: 0, bottom: 16 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tickFormatter={(d) => new Date(d).toLocaleDateString()} />
              <YAxis domain={['auto', 'auto']} />
              <Tooltip
                labelFormatter={(d) => new Date(d).toLocaleDateString()}
                formatter={(value, name) => {
                  if (name === 'bmi' && typeof value === 'number') {
                    return [value.toFixed(2), t('bodyweight.bmi')];
                  }
                  return [value, name];
                }}
              />
              <Line type="monotone" dataKey="bmi" stroke="#8884d8" dot />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  );
}; 